import { useContext, useState, useEffect, DragEvent } from "react";

import { Box, Typography, IconButton, Tab, Tabs } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  SofaElementCategoriesData,
  SofaElementCategory,
  SofaType,
  SofaColor,
  ElementsData,
  SofaElement,
} from "./DataTypes";
import { client } from "../RpcClient";
import { config } from "../Config";
import { curOptionsToExternal } from "./Options";
import { Trans } from "../Translations";
import TextOptionsChooser from "../TextOptionsChooser";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface GroupedElements {
  category: SofaElementCategory;
  elements: SofaElement[];
}

interface GroupedElementsWithIndex {
  index: number;
  catData: GroupedElements;
}

function disableScroll(e: any) {
  e.preventDefault();
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ padding: { xs: 0, lg: "20px 0" } }}>{children}</Box>
      )}
    </div>
  );
}

function allyProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function ElementWidget(props: {
  element: SofaElement;
  curColor: SofaColor | undefined;
}) {
  const { dynTr } = useContext(Trans);
  const element = props.element;
  const norm = (x: number) => Math.round(x).toString();
  return (
    <Box
      key={element.id}
      sx={{
        cursor: "grab",
      }}
      draggable="true"
      onDragStart={(ev: DragEvent<HTMLImageElement>) => {
        document.addEventListener("touchmove", disableScroll, {
          passive: false,
        });
        console.log(`DRAG STARTED: ${element.external_id}`);
        ev.dataTransfer.setData(
          "application/ree-element-json",
          JSON.stringify(element)
        );
      }}
      onDragEnd={(ev: DragEvent<HTMLImageElement>) => {
        document.removeEventListener("touchmove", disableScroll);
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 0,
          paddingTop: "100%",
        }}
      >
        <Box
          component="img"
          // src="imgs/no_image.png"
          src={`ree_renders_webp/${config.spaceId}/${element.external_id}-${props.curColor?.external_id}.webp`}
          sx={{
            pointerEvents: "none",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
          }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: '"Montserrat", sans-serif',
          fontSize: { xs: "0.6em", lg: "0.8em" },
          fontWeight: 300,
          lineHeight: 1,
          color: "#808080",
          textAlign: "center",
        }}
      >
        {dynTr(element.translations, (it) => it.name)}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Montserrat", sans-serif',
          fontSize: { xs: "0.5em", lg: "0.7em" },
          fontWeight: 300,
          lineHeight: 1.6,
          color: "#808080",
          textAlign: "center",
        }}
      >
        {`${norm(element.length)} x ${norm(element.width)}`}
      </Typography>
    </Box>
  );
}

function ElementChooserList(props: {
  elements: SofaElement[];
  curColor: SofaColor | undefined;
}) {
  const { dynTr } = useContext(Trans);
  const theme = useTheme();
  const lgOrLarger = useMediaQuery(theme.breakpoints.up("lg"));
  const isPortrait = useMediaQuery("@media (orientation: portrait)");
  const [curScroll, setCurScroll] = useState<number>(0);
  const itemsPerPage = isPortrait ? 5 : lgOrLarger ? 4 : 3;
  return (
    <Box
      sx={{
        margin: "0 25px",
        position: "relative",
        display: "grid",
        gridTemplateColumns: `repeat(${itemsPerPage}, 1fr)`,
        gridTemplateRows: "1fr",
        gridColumnGap: { xs: "10px", xl: "25px" },
        gridRowGap: { xs: "10px", xl: "25px" },
      }}
    >
      {curScroll > 0 && (
        <IconButton
          sx={{
            padding: 0,
            position: "absolute",
            top: "50%",
            left: "-15px",
            transform: "translate(-50%, -50%)",
          }}
          onClick={() => {
            setCurScroll(curScroll - 1);
          }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
      )}
      {props.elements
        .slice(curScroll, curScroll + itemsPerPage)
        .map((element) => {
          return <ElementWidget element={element} curColor={props.curColor} />;
        })}
      {curScroll + itemsPerPage < props.elements.length && (
        <IconButton
          sx={{
            padding: 0,
            position: "absolute",
            top: "50%",
            right: "-12.5px",
            transform: "translate(50%, -50%)",
          }}
          onClick={() => {
            setCurScroll(curScroll + 1);
          }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      )}
    </Box>
  );
}

export default function ElementChooser(props: {
  curType: SofaType | undefined;
  curColor: SofaColor | undefined;
  curOptions: Array<{ groupId: number; optionId: number }>;
  elementsData: ElementsData | undefined;
}) {
  const { dynTr } = useContext(Trans);
  const theme = useTheme();
  const lgOrLarger = useMediaQuery(theme.breakpoints.up("lg"));
  const [tabsValue, setTabsValue] = useState(0);
  const [elementCategories, setElementCategories] = useState<{
    data: SofaElementCategory[];
  }>();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabsValue(newValue);
  };
  useEffect(() => {
    const getApiData = async () => {
      const response: SofaElementCategoriesData = await fetch(
        `${config.apiUrl}/items/sofa_element_categories?fields=*,translations.*`
      ).then((response) => response.json());
      setElementCategories(response);
    };
    getApiData();
  }, []);
  useEffect(() => {
    const curType = props.curType;
    if (curType !== undefined) {
      const player = document.getElementById("ree-player");
      if (player === null) {
        console.error();
        return;
      }
      player.ondragenter = function (this, ev): any {
        ev.preventDefault();
        const data = ev.dataTransfer?.getData("application/ree-element-json");
        console.log(`DRAG ENTER: ${data}`);
      };
      player.ondragleave = function (this, ev): any {
        ev.preventDefault();
        const data = ev.dataTransfer?.getData("application/ree-element-json");
        console.log(`DRAG LEAVE: ${data}`);
      };
      player.ondragover = function (this, ev): any {
        ev.preventDefault();
        // const data = ev.dataTransfer?.getData("application/ree-element-json");
        console.log(`DRAG OVER: ${ev.clientX} x ${ev.clientY}`);
      };
      player.ondrop = function (this, ev): any {
        ev.preventDefault();
        const data = ev.dataTransfer?.getData("application/ree-element-json");
        if (data == null) {
          console.error();
          return;
        }
        const element = JSON.parse(data) as SofaElement;
        console.log(`DROPPED: ${data}`);
        const bounds = player.getBoundingClientRect();
        console.log(bounds);
        const curColor = props.curColor;
        if (curColor === undefined) {
          console.error();
          return;
        }
        const event = {
          element_id: element.id,
          external_id: element.external_id,
          color_id: curColor.id,
          color_external_id: curColor.external_id,
          mouse_position_x: ev.pageX - bounds.x,
          mouse_position_y: ev.pageY - bounds.y,
          rotation: element.initial_rotation ?? 0,
          state_id:
            element.states.length > 0
              ? element.states.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))[0]
                  .id
              : -1,
          state_external_id:
            element.states.length > 0 ? element.states[0].external_id : "",
          options: curOptionsToExternal(props.curOptions, curType),
        };
        console.log(
          `XY: ${event.mouse_position_x} x ${event.mouse_position_y}`
        );
        client.request("SofaOnDrop", event);
        /*
           const newUserPresets = userTypes[curType] ?? {
maxCount: 1,
types: [],
};
newUserPresets.types.push({
id: Date.now(),
name: curType.name,
elements: [],
});
setUserTypes({ ...userTypes, [curType.id]: newUserPresets });
*/
      };
    }
  }, [props]);
  if (elementCategories === undefined) {
    return <Box></Box>;
  }
  const groupedByCategories: GroupedElements[] = [];
  for (const element of props.elementsData?.data ?? []) {
    if (
      (config.allElementsInAllTypes ?? false) ||
      element.sofa_type === props.curType?.id
    ) {
      const categoryObj = groupedByCategories.find(
        (it) => it.category.id === element.category
      );
      if (categoryObj === undefined) {
        const category = elementCategories.data.find(
          (it) => it.id === element.category
        );
        if (category === undefined) {
          console.log(category, element.category, elementCategories);
          throw Error();
        }
        groupedByCategories.push({ category: category, elements: [element] });
      } else {
        categoryObj.elements.push(element);
      }
    }
  }
  groupedByCategories.sort((it, it2) => it.category.sort - it2.category.sort);
  console.log(props.elementsData);
  console.log(groupedByCategories);
  if (config.nonScrollableCategories === true) {
    return (
      <Box sx={{ marginTop: "30px" }}>
        <TextOptionsChooser<GroupedElementsWithIndex>
          options={groupedByCategories.map((catData, index) => ({
            catData,
            index,
          }))}
          getTitle={(option) =>
            dynTr(option.catData.category.translations, (it) => it.name)
          }
          getId={(option) => option.index}
          selectedOption={{
            index: tabsValue,
            catData: groupedByCategories[tabsValue],
          }}
          setSelectedOption={(option) => setTabsValue(option.index)}
          isPopup={false}
          columns={2}
        />
        <Box sx={{ marginTop: "30px" }}>
          {groupedByCategories[tabsValue].elements.map((element) => {
            return (
              <Box sx={{ width: "25%", display: "inline-block" }}>
                <ElementWidget element={element} curColor={props.curColor} />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          margin: {
            xs: "0 -20px 10px -20px",
            lg: "10px 0 0 0",
          },
        }}
      >
        <Box sx={{ width: "100%", typography: "body1" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabsValue}
              onChange={handleChange}
              variant={lgOrLarger ? "fullWidth" : "scrollable"}
              scrollButtons={!lgOrLarger}
              allowScrollButtonsMobile={!lgOrLarger}
              sx={{
                "& .MuiTabScrollButton-root": {
                  width: "20px",
                },
                minHeight: {
                  xs: "24px",
                  lg: "48px",
                },
              }}
            >
              {groupedByCategories.map((catData, index) => {
                return (
                  <Tab
                    sx={{
                      fontFamily: '"Montserrat", sans-serif',
                      fontSize: { xs: "10px", lg: "14px" },
                      minHeight: { xs: "24px", lg: "48px" },
                    }}
                    key={catData.category.id}
                    label={dynTr(
                      catData.category.translations,
                      (it) => it.name
                    )}
                    {...allyProps(index)}
                  />
                );
              })}
            </Tabs>
          </Box>
          {groupedByCategories.map((catData, index) => {
            return (
              <TabPanel
                key={catData.category.id}
                value={tabsValue}
                index={index}
              >
                <ElementChooserList
                  elements={catData.elements}
                  curColor={props.curColor}
                />
              </TabPanel>
            );
          })}
        </Box>
      </Box>
    );
  }
}
